import type {
  KumaStatusPageResponse,
  KumaHeartbeatResponse,
  KumaHeartbeat,
  KumaIncident,
  KumaMaintenance,
} from "../types";

/**
 * Generates an array of heartbeats ending at the current time.
 */
function generateHeartbeats(
  count: number,
  options: {
    statusOverride?: (index: number) => number;
    basePing?: number;
    jitter?: number;
  } = {}
): KumaHeartbeat[] {
  const { statusOverride, basePing = 45, jitter = 15 } = options;
  const now = Date.now();
  const stepMs = 60 * 1000; // 1 minute per heartbeat
  const list: KumaHeartbeat[] = [];

  for (let i = count - 1; i >= 0; i--) {
    const time = new Date(now - i * stepMs).toISOString().replace("T", " ").slice(0, 23);
    const status = statusOverride ? statusOverride(count - 1 - i) : 1;
    const ping =
      status === 1
        ? Number((basePing + (Math.random() * 2 - 1) * jitter).toFixed(2))
        : undefined;

    list.push({
      status,
      time,
      msg: status === 0 ? "Connection timeout" : "",
      ping,
    });
  }

  return list;
}

export const mockOperationalStatusPage: KumaStatusPageResponse = {
  config: {
    slug: "legalease",
    title: "LegalEase Rendszerállapot",
    description: "A LegalEase szolgáltatások és infrastruktúra valós idejű állapota",
    theme: "auto",
    published: true,
  },
  incident: null,
  publicGroupList: [
    {
      id: 1,
      name: "LegalEase Rendszermag & Web",
      weight: 1,
      monitorList: [
        { id: 101, name: "LegalEase Web Alkalmazás (app.legalease.hu)", type: "keyword" },
        { id: 102, name: "LegalEase Marketing Portál", type: "keyword" },
        { id: 103, name: "Ügyfélkapu Hitelesítés & SSO", type: "http" },
      ],
    },
    {
      id: 2,
      name: "AI & Intelligens Jogi Szolgáltatások",
      weight: 2,
      monitorList: [
        { id: 201, name: "Jogi Elemző & Chat Ügynök Futtatók", type: "http" },
        { id: 202, name: "PII Adatvédelmi Maszkoló Motor", type: "http" },
        { id: 203, name: "Intelligens Jogi Diktálás (Whisper)", type: "http" },
      ],
    },
    {
      id: 3,
      name: "Adatbázisok & Korpusz Keresők",
      weight: 3,
      monitorList: [
        { id: 301, name: "NJT Magyar Jogtár & Vektorkereső (Lawz)", type: "http" },
        { id: 302, name: "Dokumentum Generáló & Docx Export", type: "http" },
        { id: 303, name: "Aszinkron Háttér Worker & Értesítések", type: "http" },
      ],
    },
  ],
  maintenanceList: [],
};

export const mockOperationalHeartbeats: KumaHeartbeatResponse = {
  heartbeatList: {
    "101": generateHeartbeats(40, { basePing: 32, jitter: 8 }),
    "102": generateHeartbeats(40, { basePing: 18, jitter: 5 }),
    "103": generateHeartbeats(40, { basePing: 25, jitter: 6 }),
    "201": generateHeartbeats(40, { basePing: 55, jitter: 12 }),
    "202": generateHeartbeats(40, { basePing: 42, jitter: 10 }),
    "203": generateHeartbeats(40, { basePing: 85, jitter: 20 }),
    "301": generateHeartbeats(40, { basePing: 28, jitter: 7 }),
    "302": generateHeartbeats(40, { basePing: 65, jitter: 15 }),
    "303": generateHeartbeats(40, { basePing: 15, jitter: 4 }),
  },
  uptimeList: {
    "101_24": 1.0,
    "102_24": 1.0,
    "103_24": 0.9998,
    "201_24": 0.9995,
    "202_24": 1.0,
    "203_24": 0.9985,
    "301_24": 1.0,
    "302_24": 0.9992,
    "303_24": 1.0,
  },
};

export const mockDegradedIncident: KumaIncident = {
  id: 42,
  title: "Időszakos lassulás tapasztalható a Jogi Diktálás szolgáltatásban",
  content:
    "Mérnökeink jelenleg vizsgálják az audio transzkripciós háttérfeldolgozó megnövekedett válaszidejét. A szerződéskészítés és az egyéb alapvető funkciók zavartalanul elérhetők.",
  style: "warning",
  createdDate: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
  lastUpdatedDate: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
};

export const mockMaintenance: KumaMaintenance = {
  id: 7,
  title: "Tervezett infrastruktúra-karbantartás",
  description:
    "Rendszerkarbantartási munkálatok miatt a hétvégén (vasárnap 02:00 - 04:00 CET) a dokumentum-előnézet rövid ideig szünetelhet.",
  start: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
  end: new Date(Date.now() + 26 * 3600 * 1000).toISOString(),
  active: true,
};
